// ActionCard — single proposed-action tile in the ActionsPanel right rail.
//
// Variants:
//   primary   — featured card at top with RECOMMENDED chip, preview text block,
//               "Send & update order" primary CTA + "Tweak copy" secondary CTA.
//   secondary — compact card with title + description + "Use this" CTA only.
//
// Keyboard: the parent binds 1 / 2 / 3 to fire the corresponding card. The card
// itself doesn't listen — it just reports its action id via onFire.

import type { ProposedAction } from '@/lib/hooks/useProposedActions';

interface ActionCardProps {
  action: ProposedAction;
  variant: 'primary' | 'secondary';
  index: number; // 1-based for the 1/2/3 keyboard hint
  onFire: (action: ProposedAction) => void;
  onTweak: (action: ProposedAction) => void;
  busy: boolean;
}

export function ActionCard({ action, variant, index, onFire, onTweak, busy }: ActionCardProps) {
  const isPrimary = variant === 'primary';
  return (
    <article className={`action-card ${isPrimary ? 'primary' : ''}`}>
      <div className="a-top">
        <div className="a-heading">
          <span className="a-kbd" aria-hidden>
            {index}
          </span>
          <div>
            <div className="a-title">{action.title}</div>
            <div className="a-sub">{action.description}</div>
          </div>
        </div>
        {isPrimary ? <span className="tag">RECOMMENDED</span> : null}
      </div>

      {isPrimary && action.preview ? (
        <div className="a-preview">{action.preview}</div>
      ) : null}

      <div className="a-actions">
        <button
          type="button"
          className={isPrimary ? 'btn primary' : 'btn sm'}
          disabled={busy}
          onClick={() => onFire(action)}
        >
          {isPrimary ? 'Send & apply' : 'Use this'}
        </button>
        {isPrimary ? (
          <button
            type="button"
            className="btn sm"
            onClick={() => onTweak(action)}
            disabled={busy}
          >
            Tweak copy
          </button>
        ) : null}
      </div>
    </article>
  );
}
